import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';

const TYPE_COLOR = {
  qualitative: { bg: '#eef7ee', color: '#2e7d32' },   // 정성
  quantitative: { bg: '#eef2fb', color: '#1a56b0' },  // 정량
  mixed: { bg: '#faf3e6', color: '#a06a00' },         // 혼합
};

function Badge({ children, bg = '#f0f0f0', color = '#555' }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: 12, background: bg, color, marginRight: 6,
    }}>
      {children}
    </span>
  );
}

function IndicatorCard({ ind, onOpenPdf, memos = {}, depth = 0 }) {
  const [open, setOpen] = useState(false);
  const typeStyle = TYPE_COLOR[ind.indicator_type] ?? {};
  const pageMemos = memos[ind.pdf_page] ?? [];
  



  return (
    <div style={{
      border: '1px solid #e2e2e2', borderRadius: 8, padding: '12px 16px',
      marginBottom: 10, marginLeft: depth * 28, background: '#fff',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <strong style={{ fontSize: 15 }}>{ind.name}</strong>{' '}
          <Badge bg={typeStyle.bg} color={typeStyle.color}>{ind.indicator_type_display}</Badge>
          <Badge>{ind.evaluation_method_display}</Badge>

          {ind.is_deduction && <Badge bg="#fdecea" color="#c62828">감점</Badge>}
          {ind.direction === 'down' && <Badge bg="#f3e8fd" color="#6a1b9a">하향지표</Badge>}
          {pageMemos.length > 0 && (
            <Badge bg="#fff7e6" color="#e08a00">메모 {pageMemos.length}</Badge>
          )}


        </div>
        <div style={{ whiteSpace: 'nowrap' }}>
          <strong style={{ color: ind.points < 0 ? '#c62828' : '#1a56b0' }}>
            {ind.points}점
          </strong>
        </div>
      </div>

      {ind.definition && (
        <p style={{ margin: '8px 0 4px', fontSize: 13, color: '#444', lineHeight: 1.6 }}>
          {ind.definition}
        </p>
      )}

      {open && (
        <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.7 }}>
          {ind.formula && (
            <div style={{ background: '#f7f9fc', borderRadius: 6, padding: '8px 12px', marginBottom: 6 }}>
              <span style={{ color: '#888' }}>산식 · 목표부여</span><br />
              {ind.formula}
            </div>
          )}

          {ind.detail && (
            <div style={{ background: '#fbfbf7', borderRadius: 6, padding: '8px 12px' }}>
              <span style={{ color: '#888' }}>세부평가방법</span><br />
              {ind.detail}
            </div>
          )}
        </div>
      )}



      <div style={{ marginTop: 8, fontSize: 12 }}>
        {(ind.formula || ind.detail) && (

          <button
            onClick={() => setOpen((v) => !v)}


            style={{ display: 'inline-block', width: 'auto', padding: '4px 10px', fontSize: 12, marginRight: 8 }}
          >
            {open ? '접기' : '자세히'}
          </button>
        )}

        {ind.page_number && (
          <button
            onClick={() => onOpenPdf(ind.pdf_page)}
            style={{ display: 'inline-block', width: 'auto', padding: '4px 10px', fontSize: 12 }}
          >
            원문 p.{ind.page_number} 보기
          </button>
        )}
      </div>

      {pageMemos.length > 0 && (
        <div style={{ marginTop: 10, borderTop: '1px dashed #e8ddc8', paddingTop: 8 }}>
          <span style={{ color: '#e08a00', fontSize: 12, fontWeight: 600 }}>
            📝 메모 {pageMemos.length}
          </span>

          {pageMemos.map((m) => (
            <div
              key={m.id}
              style={{
                border: '1px solid #f0e2c8', borderRadius: 6, padding: '8px 12px',
                background: m.is_deleted ? '#f5f5f5' : '#fffdf5', marginTop: 6,
                fontSize: 13, lineHeight: 1.6, opacity: m.is_deleted ? 0.65 : 1,
              }}
            >
              <div style={{
                whiteSpace: 'pre-wrap',
                textDecoration: m.is_deleted ? 'line-through' : 'none',
                color: m.is_deleted ? '#999' : 'inherit',
              }}>
                {m.content}
              </div>
              <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                {m.created_by_name || '익명'} · {new Date(m.created_at).toLocaleString()}
                {m.is_deleted && <span style={{ marginLeft: 6, color: '#bbb' }}>· 삭제됨</span>}
              </div>
            </div>
          ))}


        </div>
      )}

      
      {ind.sub_indicators?.map((sub) => (
        <div key={sub.id} style={{ marginTop: 10 }}>
          <IndicatorCard ind={sub} onOpenPdf={onOpenPdf} memos={memos} depth={0} />
        </div>
      ))}


    </div>
  );
}

/**
 * 경영평가 > 평가지표 정리 보기
 * - 평가영역(경영관리/경영성과) → 평가부문(수익성, 안정성…) 탭 필터
 * - 지표명 검색, 정성/정량 필터
 * - "원문 보기" 클릭 시 PDF 뷰어의 해당 페이지로 이동
 */
export default function EvaluationIndicatorBoard() {
  const { id } = useParams(); // manual id
  const navigate = useNavigate();
  const [manual, setManual] = useState(null);
  const [area, setArea] = useState('all');
  const [catId, setCatId] = useState('all');

  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');
  const [memos, setMemos] = useState({}); // { [pdf_page]: [메모, ...] }

  useEffect(() => {
    api.get(`evaluation/manuals/${id}/`)
      .then((res) => setManual(res.data))
      .catch((e) => { console.error(e); alert('편람 정보를 불러오지 못했습니다.'); });
  }, [id]);

  useEffect(() => {
    api.get('evaluation/memos/', { params: { manual: id } })
      .then((res) => {
        const list = res.data.results ?? res.data;
        const map = {};
        list.forEach((m) => {
          if (!map[m.page_number]) map[m.page_number] = [];
          map[m.page_number].push(m);
        });
        setMemos(map);
      })
      .catch((e) => console.error(e));
  }, [id]);



  const categories = manual?.categories ?? [];

  const visibleCategories = useMemo(() => {
    return categories
      .filter((c) => area === 'all' || c.area === area)
      .filter((c) => catId === 'all' || String(c.id) === String(catId))
      .map((c) => ({
        ...c,
        indicators: c.indicators
          .filter((ind) => {
            const matchType = type === 'all'
              || ind.indicator_type === type
              || ind.sub_indicators?.some((s) => s.indicator_type === type);
            const q = search.trim();
            const matchSearch = !q
              || ind.name.includes(q)
              || ind.definition?.includes(q)
              || ind.sub_indicators?.some((s) => s.name.includes(q));
            return matchType && matchSearch;
          }),
      }))
      .filter((c) => c.indicators.length > 0);
  }, [categories, area, catId, type, search]);

  const openPdf = (page) => {
    navigate(`/evaluation/manuals/${id}/viewer?page=${page ?? 1}`);
  };

  if (!manual) return <div style={{ padding: 24 }}>불러오는 중…</div>;

  const totalPoints = categories
    .filter((c) => c.area !== 'ceo')
    .reduce((sum, c) => sum + Number(c.points), 0);

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto', background: '#fafafa' }}>
      <h2 style={{ marginTop: 0 }}>
        {manual.year}년({manual.target_year}년도 실적) 평가지표
        <span style={{ fontSize: 14, color: '#888', marginLeft: 10 }}>총 {totalPoints}점</span>
      </h2>

      {/* 평가영역 탭 */}
      <div style={{ marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {[['all', '전체'], ['management', '경영관리 (50점)'], ['performance', '경영성과 (50점)']].map(([v, label]) => (
          <button
            key={v}
            onClick={() => { setArea(v); setCatId('all'); }}
            style={{
              display: 'inline-block',
              width: 'auto',
              flex: 'none',
              padding: '6px 14px',
              fontSize: 13,
              whiteSpace: 'nowrap',
              borderRadius: 16,
              border: '1px solid #ccc',
              cursor: 'pointer',
              background: area === v ? '#1a56b0' : '#fff',
              color: area === v ? '#fff' : '#333',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 평가부문 탭 (수익성만 보기 / 안정성만 보기 등) */}
      <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <button
          onClick={() => setCatId('all')}
          style={{
            display: 'inline-block', width: 'auto', flex: 'none',
            padding: '4px 12px', fontSize: 13, whiteSpace: 'nowrap',
            borderRadius: 14, border: '1px solid #ddd', cursor: 'pointer',
            background: catId === 'all' ? '#444' : '#fff',
            color: catId === 'all' ? '#fff' : '#333',
          }}
        >
          전체 부문
        </button>
        {categories
          .filter((c) => area === 'all' || c.area === area)
          .map((c) => (
            <button
              key={c.id}
              onClick={() => setCatId(String(c.id))}
              style={{
                display: 'inline-block', width: 'auto', flex: 'none',
                padding: '4px 12px', fontSize: 13, whiteSpace: 'nowrap',
                borderRadius: 14, border: '1px solid #ddd', cursor: 'pointer',
                background: String(c.id) === catId ? '#444' : '#fff',
                color: String(c.id) === catId ? '#fff' : '#333',
              }}
            >
              {c.name} ({c.points}점)
            </button>
          ))}
      </div>

      {/* 검색/필터 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="지표명·정의 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 0, padding: '6px 10px', boxSizing: 'border-box' }}
        />
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          style={{ width: 'auto', flex: 'none', padding: '6px 10px', fontSize: 13 }}
        >
          <option value="all">성격 전체</option>
          <option value="qualitative">정성</option>
          <option value="quantitative">정량</option>
          <option value="mixed">혼합</option>
        </select>
      </div>

      {/* 부문별 지표 카드 */}
      {visibleCategories.length === 0 && (
        <p style={{ color: '#888' }}>조건에 맞는 지표가 없습니다.</p>
      )}
      {visibleCategories.map((cat) => (
        <section key={cat.id} style={{ marginBottom: 24 }}>
          <h3 style={{ borderBottom: '2px solid #333', paddingBottom: 6 }}>
            <span style={{ fontSize: 13, color: '#888', marginRight: 8 }}>{cat.area_display}</span>
            {cat.name}
            <span style={{ fontSize: 13, color: '#1a56b0', marginLeft: 8 }}>{cat.points}점</span>
          </h3>
          {cat.indicators.map((ind) => (
            <IndicatorCard key={ind.id} ind={ind} onOpenPdf={openPdf} memos={memos} />
          ))}
          


        </section>
      ))}
    </div>
  );
}